import { cn, Skeleton, Tooltip } from '@heroui/react';
import { Settings } from 'iconoir-react';

import i18n from '~/common/helpers/i18n';
import {
  DeploymentIssueIndicator,
  DeploymentIssueInput,
  getDeploymentIssueSummary
} from '~/components/DeploymentIssueIndicator';
import {
  getMessageStatus,
  shortenEngineVersion,
  StatusKey
} from '~/scenes/Runtime/scenes/Deployments/utils/deploymentUtils';

const STATUS_ICON_COLORS: Partial<Record<StatusKey, string>> = {
  deployed: 'text-positive',
  service_active: 'text-positive',
  running: 'text-positive',
  redeployed: 'text-positive',
  error: 'text-negative',
  service_error: 'text-negative',
  deleting: 'text-negative',
  starting: 'text-blue-500 dark:text-blue-300',
  degraded: 'text-blue-500 dark:text-blue-300',
  redeploy: 'text-blue-500 dark:text-blue-300',
  service_active_but_degraded: 'text-blue-500 dark:text-blue-300'
};

export type DeployStatusCellProps = {
  status?: StatusKey;
  /** Engine version the deployment runs on, shown under the status. */
  engineVersion?: string;
  /** Optional: when given, a problem on the deployment is flagged next to the status. */
  deployment?: DeploymentIssueInput | null;
};

export type { StatusKey };

export const DeployStatusCell = ({
  status,
  engineVersion,
  deployment
}: DeployStatusCellProps) => {
  if (!status) return null;

  const { icon: IconComponent, text } = getMessageStatus({
    deployStatus: status
  });

  const issueSummary = getDeploymentIssueSummary(deployment);

  return (
    <div className='flex w-[140px] items-center gap-3'>
      <IconComponent className={cn('icon-small', STATUS_ICON_COLORS[status])} />
      <div className='flex flex-col gap-1'>
        {/* Beside the status text, not under it: one indicator fits on the same line, and the
            column keeps its width. The row only exists when there is a problem, so a cell
            without one keeps the markup it always had. */}
        {issueSummary ? (
          <div className='flex items-center gap-1'>
            <p>{text}</p>
            <DeploymentIssueIndicator summary={issueSummary} />
          </div>
        ) : (
          <p>{text}</p>
        )}
        {engineVersion ? (
          <Tooltip
            content={i18n.t('scenes.runtime.labels.engine_version')}
            placement='bottom'
          >
            <span
              data-testid='deploy-status-engine-version'
              className='flex w-fit items-center gap-1 font-mono text-label-md tracking-wider text-on-surface-variant'
            >
              <Settings aria-hidden className='icon-smallest' />
              <span className='sr-only'>
                {i18n.t('scenes.runtime.labels.engine_version')}
              </span>
              {shortenEngineVersion(engineVersion)}
            </span>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
};

export const DeployStatusCellSkeleton = () => (
  <div className='flex w-[140px] items-center gap-3'>
    <Skeleton className='h-4 w-4 rounded' />
    <div className='flex flex-col gap-1'>
      <Skeleton className='h-4 w-16 rounded' />
      <Skeleton className='h-3 w-20 rounded' />
    </div>
  </div>
);
